// PROTOTYPE: This file was scaffolded quickly and is pending a rewrite. Do not use as a model for other parts of the codebase.
import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { AssistantRuntimeProvider } from '@assistant-ui/react'
import { useAgUiRuntime } from '@assistant-ui/react-ag-ui'
import { subscribeAICreateAgUiEvents } from '@/modules/api/events'
import { AiCreateAgUiAgent } from './aiCreateAgUiAgent'

const AiCreateAgentContext = createContext<AiCreateAgUiAgent | null>(null)

export function useAiCreateAgent(): AiCreateAgUiAgent {
    const agent = useContext(AiCreateAgentContext)
    if (!agent) {
        throw new Error(
            'useAiCreateAgent must be used within AICreateRuntimeProvider',
        )
    }
    return agent
}

/**
 * assistant-ui runtime over the session's AG-UI SSE. Runs start server-side,
 * so the agent buffers each RUN_STARTED and pokes the thread to claim it via
 * startRun; there is no composer send path until the chat becomes
 * conversational.
 */
export const AICreateRuntimeProvider: React.FC<{
    sessionId: string
    children: React.ReactNode
}> = ({ sessionId, children }) => {
    const agent = useMemo(
        () =>
            new AiCreateAgUiAgent({
                sessionId,
                subscribeEvents: subscribeAICreateAgUiEvents,
            }),
        [sessionId],
    )

    useEffect(() => {
        agent.openSession()
        return () => {
            agent.closeSession()
        }
    }, [agent])

    const runtime = useAgUiRuntime({ agent, isSendDisabled: true })

    useEffect(
        () =>
            agent.subscribeRunPending(() => {
                const messages = runtime.thread.getState().messages
                runtime.thread.startRun({
                    parentId: messages.at(-1)?.id ?? null,
                })
            }),
        [agent, runtime],
    )

    return (
        <AiCreateAgentContext.Provider value={agent}>
            <AssistantRuntimeProvider runtime={runtime}>
                {children}
            </AssistantRuntimeProvider>
        </AiCreateAgentContext.Provider>
    )
}
